import { atomWithStorage } from "jotai/utils";
import { z } from "zod";

import { getDeploymentType } from "~/shared/utils/get-deployment-type.ts";

import { bulkQuoteBatchStatusValueSchema } from "../schemas/bulk-quote-batch-status-schema.ts";

export const ACTIVE_BULK_QUOTE_BATCH_STORAGE_KEY =
	"quote-board-active-bulk-quote-batch";

export const activeBulkQuoteBatchSchema = z.object({
	bulkQuoteId: z.string().min(1),
	/**
	 * Identity that uploaded the batch. Logout intentionally leaves session
	 * storage intact (see `use-is-authenticated.tsx`), so a second user signing
	 * in to the same tab would otherwise inherit — and poll — this batch.
	 */
	ownerId: z.string().min(1),
	/**
	 * Customer profile the batch was uploaded under. External requests carry
	 * `Tql-Identity-Companyid`, so the status endpoint only answers for this
	 * profile; polling it from another one fails. Internal requests are not
	 * customer scoped and therefore track `null`. Snapshots written before this
	 * field existed also read back as `null`.
	 */
	customerId: z.string().nullable().default(null),
	status: bulkQuoteBatchStatusValueSchema.nullable(),
	processedCount: z.number().int().nonnegative().nullable(),
	requestCount: z.number().int().nonnegative().nullable(),
	errorCount: z.number().int().nonnegative().nullable(),
	/** Terminal status the user was already toasted about, so revisits stay quiet. */
	notifiedStatus: bulkQuoteBatchStatusValueSchema.nullable(),
	isDismissed: z.boolean(),
});

export type ActiveBulkQuoteBatch = z.infer<typeof activeBulkQuoteBatchSchema>;
type StoredBulkQuoteBatches = Record<string, ActiveBulkQuoteBatch>;

export const createActiveBulkQuoteBatch = (
	bulkQuoteId: string,
	ownerId: string,
	customerId: string | null = null,
): ActiveBulkQuoteBatch => ({
	bulkQuoteId,
	ownerId,
	customerId,
	status: null,
	processedCount: null,
	requestCount: null,
	errorCount: null,
	notifiedStatus: null,
	isDismissed: false,
});

/**
 * Resolves a stable per-user key from the authenticated identity. Internal
 * (Azure AD) users carry `employeeid`, external (B2C) users carry `com.tql.id`,
 * and `email` backstops tokens that expose neither. Returns `null` while the
 * identity is still resolving so callers can wait instead of guessing.
 */
export const resolveBulkQuoteBatchOwnerId = (identity: {
	employeeId?: string;
	userGuid?: string;
	email?: string;
}): string | null =>
	identity.employeeId || identity.userGuid || identity.email || null;

/**
 * A persisted batch is usable only by the identity that created it. An unknown
 * current owner never matches, so the banner stays hidden until identity
 * resolves rather than leaking the previous user's batch.
 */
export const isBulkQuoteBatchOwnedBy = (
	batch: ActiveBulkQuoteBatch | null,
	ownerId: string | null,
): boolean => batch != null && ownerId != null && batch.ownerId === ownerId;

/**
 * A batch can only be polled from the customer profile that uploaded it, because
 * the status request is scoped by the active customer headers. Switching
 * profiles reloads the tab but keeps session storage, so a mismatch hides the
 * batch instead of discarding it and the banner resumes when the user returns to
 * the owning profile.
 */
export const isBulkQuoteBatchForCustomer = (
	batch: ActiveBulkQuoteBatch | null,
	customerId: string | null,
): boolean => batch != null && batch.customerId === customerId;

// Validate on read: sessionStorage can hold a stale or hand-edited value from an
// older snapshot shape. Falling back to "no active batch" keeps the Quote Board
// from rendering a banner it cannot describe.
const getSessionStorage = (): Storage | undefined => {
	try {
		return globalThis.sessionStorage;
	} catch {
		return undefined;
	}
};

const getBatchStorageScope = (customerId: string | null): string =>
	customerId == null ? "internal" : `customer:${customerId}`;

const getStoredBatchScope = (): string => {
	if (getDeploymentType() === "internal") {
		return getBatchStorageScope(null);
	}

	try {
		const raw = globalThis.sessionStorage?.getItem("selectedCustomers");
		if (raw == null) {
			return getBatchStorageScope(null);
		}

		const selectedCustomer = JSON.parse(raw) as { id?: string | number };
		return getBatchStorageScope(
			selectedCustomer.id == null ? null : String(selectedCustomer.id),
		);
	} catch {
		return getBatchStorageScope(null);
	}
};

const storedBulkQuoteBatchesSchema = z.record(activeBulkQuoteBatchSchema);

/**
 * Keeps whatever scoped entries still validate and drops the rest, so one
 * corrupt profile entry cannot strand the batches belonging to other profiles.
 */
const salvageStoredBatches = (parsed: unknown): StoredBulkQuoteBatches => {
	if (typeof parsed !== "object" || parsed == null || Array.isArray(parsed)) {
		return {};
	}

	const salvaged: StoredBulkQuoteBatches = {};
	for (const [scope, candidate] of Object.entries(parsed)) {
		const batch = activeBulkQuoteBatchSchema.safeParse(candidate);
		if (batch.success) {
			salvaged[scope] = batch.data;
		}
	}

	return salvaged;
};

/**
 * Always resolves to a usable map. Unreadable storage degrades to "no batches"
 * rather than a sentinel the callers must guard, so the next valid write or
 * clear repairs the key instead of being blocked by it.
 */
const readStoredBatches = (stored: string | null): StoredBulkQuoteBatches => {
	if (stored == null) {
		return {};
	}

	let parsed: unknown;
	try {
		parsed = JSON.parse(stored);
	} catch {
		return {};
	}

	const batches = storedBulkQuoteBatchesSchema.safeParse(parsed);
	if (batches.success) {
		return batches.data;
	}

	// The previous format held one batch for the entire tab. Retain it under
	// its known scope, allowing an internal legacy batch to continue working
	// while avoiding an external batch being shown for every customer.
	const legacyBatch = activeBulkQuoteBatchSchema.safeParse(parsed);
	if (legacyBatch.success) {
		return {
			[getBatchStorageScope(legacyBatch.data.customerId)]: legacyBatch.data,
		};
	}

	return salvageStoredBatches(parsed);
};

const validatedSessionStorage = {
	getItem: (
		key: string,
		initialValue: ActiveBulkQuoteBatch | null,
	): ActiveBulkQuoteBatch | null => {
		const batches = readStoredBatches(
			getSessionStorage()?.getItem(key) ?? null,
		);
		return batches[getStoredBatchScope()] ?? initialValue;
	},
	setItem: (key: string, value: ActiveBulkQuoteBatch | null): void => {
		const storage = getSessionStorage();
		if (storage == null) {
			return;
		}

		const batches = readStoredBatches(storage.getItem(key));
		const scope = getStoredBatchScope();
		if (value == null) {
			delete batches[scope];
		} else {
			batches[getBatchStorageScope(value.customerId)] = value;
		}

		if (Object.keys(batches).length === 0) {
			storage.removeItem(key);
			return;
		}

		storage.setItem(key, JSON.stringify(batches));
	},
	removeItem: (key: string): void => {
		const storage = getSessionStorage();
		if (storage == null) {
			return;
		}

		const batches = readStoredBatches(storage.getItem(key));

		delete batches[getStoredBatchScope()];
		if (Object.keys(batches).length === 0) {
			storage.removeItem(key);
			return;
		}

		storage.setItem(key, JSON.stringify(batches));
	},
};

/**
 * Tracks the bulk upload/tender batch for the active profile. The persisted
 * backing value retains an independent batch per profile, so an upload for one
 * customer cannot replace an in-progress upload for another customer.
 */
export const activeBulkQuoteBatchAtom =
	atomWithStorage<ActiveBulkQuoteBatch | null>(
		ACTIVE_BULK_QUOTE_BATCH_STORAGE_KEY,
		null,
		validatedSessionStorage,
		// Hydrate synchronously so the batch is polled from the first render of a
		// return trip. The banner itself waits for that poll, so a revisit never
		// paints counts captured before the user navigated away.
		{ getOnInit: true },
	);
